import { useCallback } from 'react';

import { t, Trans } from '@lingui/macro';

import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';

import Download from '@decisiv/iconix/lib/components/Download';
import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';
import ExclamationTriangleF from '@decisiv/iconix/lib/components/ExclamationTriangleF';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import { useAddAssetContext } from '../context/AddAssetContext';
import { SummaryRow, StatusMessage } from './styles';

const SUCCESS_COLOR = toColorString(color.status.success.medium);
const DANGER_COLOR = toColorString(color.status.danger.medium);

/**
 * Summary step. Registration runs in the wizard's intermediate state (see
 * AddingAssets) before this step is revealed, so here we only present the
 * added/failed results already written to saved state.
 */
const StepFour = () => {
  const { savedState } = useAddAssetContext();

  const { accountName, depotName } = savedState.stepOne;
  const { assetsAdded, assetsFailed, reportUrl, failed } =
    savedState.registration;

  const assetsAddedAmount = assetsAdded.length;
  const assetsFailedAmount = assetsFailed.length;
  const destination = depotName || accountName;

  // Systemic failure (poll error / no results) and a run where every asset
  // failed both collapse into the "all failed" view.
  const isAllFailed = failed || assetsAddedAmount === 0;
  const isPartial = !isAllFailed && assetsFailedAmount > 0;

  const handleDownloadReportClick = useCallback(() => {
    // Defensive guard: the button is disabled when reportUrl is absent, so
    // this branch is unreachable via the UI.
    /* istanbul ignore next -- @preserve */
    if (!reportUrl) return;

    window.location.assign(reportUrl);
  }, [reportUrl]);

  return (
    <Flex flex={1} flexDirection="column">
      <H3>
        <Trans>Summary</Trans>
      </H3>
      <SummaryRow>
        <Flex alignItems="flex-start" flex={1}>
          {isAllFailed ? (
            <ExclamationTriangleF color={DANGER_COLOR} />
          ) : (
            <CheckCircleF color={SUCCESS_COLOR} />
          )}
          <StatusMessage>
            {isAllFailed ? (
              <P shade={1}>
                {assetsFailedAmount > 0
                  ? t`All ${assetsFailedAmount} assets failed to be added to ${destination}.`
                  : t`All assets failed to be added to ${destination}.`}
              </P>
            ) : (
              <>
                <P shade={1}>
                  {t`${assetsAddedAmount} Assets successfully added to ${destination}.`}
                </P>
                {isPartial && (
                  <P shade={1}>
                    {t`${assetsFailedAmount} Assets failed to be added.`}
                  </P>
                )}
              </>
            )}
          </StatusMessage>
        </Flex>
        <Button
          text={t`Download Report`}
          size="small"
          kind="secondary"
          icon={Download}
          disabled={!reportUrl}
          onClick={handleDownloadReportClick}
        />
      </SummaryRow>
    </Flex>
  );
};

export default StepFour;
